import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteAuthenticated,
  getAuthenticated,
  postAuthenticated,
  putAuthenticated,
} from './api';
import {
  AddFavoriteRequest,
  FavoriteProperties,
  HomeDesignRequest,
  HomeModelFavoriteProperties,
  HomeModelFavoriteRequest,
} from '../models/favorites.model';
import { extractIdOfSK } from '../utils/search.ts';

export const useGetFavorites = (sub: string) => {
  const url = `land/favorites/${sub}`;
  const { isPending, error, data, refetch } = useQuery<FavoriteProperties[]>({
    queryKey: ['favorites'],
    enabled: !!sub,
    queryFn: async () => {
      return getAuthenticated<FavoriteProperties[]>(url);
    },
  });

  return {
    loading: isPending,
    error,
    data: data || undefined,
    refetch,
  };
};

export const useMutateAddFavorite = () => {
  const url = `land/favorite`;
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['favorites'],
    mutationFn: async (request: AddFavoriteRequest) => {
      const body = JSON.stringify(request);
      return postAuthenticated<AddFavoriteRequest>(url, body);
    },
    onMutate: (request: AddFavoriteRequest) => {
      queryClient.setQueryData<AddFavoriteRequest[]>(['favorites'], (old) => {
        return old ? [...old, request] : [request];
      });
    },
  });
};

export const useMutateSaveHomeModelFavorite = () => {
  const url = 'home-model/favorite';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['homeModelFavorite'], // Use an array for mutationKey

    onMutate: (request: HomeModelFavoriteRequest) => {
      queryClient.setQueryData<HomeModelFavoriteProperties[]>(
        ['homeModelFavorite'],
        (old) => {
          if (!request.homeModelId) {
            return old || [];
          }

          const newFavorite: HomeModelFavoriteProperties = {
            sk: `homeModel#${request.homeModelId}`,
          };

          return old ? [...old, newFavorite] : [newFavorite];
        }
      );
    },

    mutationFn: (request: HomeModelFavoriteRequest) => {
      const body = JSON.stringify(request);
      return postAuthenticated(url, body);
    },
  });
};

export const useGetHomeModelFavorites = () => {
  const url = 'home-model/favorite';

  const { isPending, error, data, refetch } = useQuery<
    HomeModelFavoriteProperties[]
  >({
    queryKey: ['homeModelFavorite'],
    queryFn: async () => {
      return getAuthenticated<HomeModelFavoriteProperties[]>(url);
    },
  });

  return {
    loading: isPending,
    error,
    data: data,
    refetch,
  };
};
export const useMutateDeleteHomeModelFavorite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['homeModelFavorite'],
    mutationFn: async (propertyId: string) => {
      const url = `home-model/favorite?homeModelId=${propertyId}`;

      return deleteAuthenticated<HomeModelFavoriteRequest>(url);
    },
    onMutate: (propertyId: string) => {
      queryClient.setQueryData<HomeModelFavoriteProperties[]>(
        ['homeModelFavorite'],
        (old) => {
          return old?.filter(
            (favorite: HomeModelFavoriteProperties) =>
              extractIdOfSK(favorite.sk) !== propertyId
          );
        }
      );
    },
  });
};
export const useMutateDeleteHomeDesign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['homeDesign'],
    mutationFn: async (propertyId: string) => {
      console.log('propertyId', propertyId);
      const url = `home/design?homeDesignId=${propertyId}`;

      return deleteAuthenticated<HomeDesignRequest>(url);
    },
    onMutate: (propertyId: string) => {
      queryClient.setQueryData<HomeDesignRequest[]>(['homeDesign'], (old) => {
        return old?.filter((favorite) => {
          if (favorite.sk) {
            console.log('favorite', extractIdOfSK(favorite.sk) !== propertyId);
            return extractIdOfSK(favorite.sk) !== propertyId;
          }
        });
      });
    },
    onError: (error) => {
      console.error('Mutation error:', error);
    },
  });
};

export const useMutateDeleteFavorite = (sub: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['favorite'],
    mutationFn: async (propertyId: string) => {
      const url = `land/favorite/${propertyId}/?sub=${sub}`;

      return deleteAuthenticated<AddFavoriteRequest>(url);
    },
    onMutate: (propertyId: string) => {
      queryClient.setQueryData<FavoriteProperties[]>(['favorites'], (old) => {
        return old?.filter(
          (favorite: FavoriteProperties) => favorite.id !== propertyId
        );
      });
    },
  });
};

export const useMutateUpdateFavorite = () => {
  const url = `land/favorite`;
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['favorites'],
    mutationFn: async (request: AddFavoriteRequest) => {
      const body = JSON.stringify(request);
      return putAuthenticated<AddFavoriteRequest>(url, body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['favorites'],
      });
    },
  });
};
